import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { RobuxIcon, useAuth } from '../context/AuthContext';
import { useChatLayout } from '../context/ChatLayoutContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Calendar, CalendarDays, Clock, Sparkles, Flame, ShieldCheck, Lock, ChevronLeft, ChevronRight, Trophy, X, Unlock, Loader2, TrendingUp, Coins, Swords } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DynamicCasePreview } from '../components/CasePreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '0';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

// Geri sayım hesaplayıcı
const calculateTimeLeft = (targetDateStr) => {
  if (!targetDateStr) return "00:00:00";
  const target = new Date(targetDateStr).getTime();
  const now = new Date().getTime();
  const difference = target - now;

  if (difference <= 0) return "Ready to Claim!";

  const d = Math.floor(difference / (1000 * 60 * 60 * 24));
  const h = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const m = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const s = Math.floor((difference % (1000 * 60)) / 1000);

  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const calculateCooldown = (targetDateStr) => {
  if (!targetDateStr) return "Ready";
  const target     = new Date(targetDateStr).getTime();
  const difference = target - Date.now();
  if (difference <= 0) return "Ready";
  const h = Math.floor(difference / (1000 * 60 * 60));
  const m = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const s = Math.floor((difference % (1000 * 60)) / 1000);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const getWeightedRandomItem = (items) => {
  const total = items.reduce((sum, item) => sum + (item.weight || 1), 0);
  let r = Math.random() * total;
  for (const item of items) {
    r -= item.weight || 1;
    if (r <= 0) return item;
  }
  return items[0];
};

/* ──────────────────────────────────────────────────────────────
   DYNAMIC CASE PREVIEW (inlined — fully self contained)
   ────────────────────────────────────────────────────────────── */
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı: ALTI dosyada altı kopya zamanla
// ayrışıyor ve her düzeltme altı yere dokunmayı gerektiriyordu.
/* ──────────────────────────────────────────────────────────────
   HORIZONTAL SLOT SPINNER (inlined — fully self contained)
   ────────────────────────────────────────────────────────────── */
const HorizontalSlotSpinner = ({ activeCase, spunItem, shouldPlaySound }) => {
  const [strip, setStrip] = useState([]);
  const [phase, setPhase] = useState("idle");

  const randomOffset = useRef(
    (Math.random() > 0.5 ? 1 : -1) * (Math.floor(Math.random() * 30) + 12)
  );
  const spinnerRef = useRef(null);
  const audioPool  = useRef([]);
  const poolIdx    = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const pool = [];
      for (let i = 0; i < 5; i++) {
        const a = new Audio("/reel_tick.mp3");
        a.volume = 0.3;
        pool.push(a);
      }
      audioPool.current = pool;
    }
  }, []);

  const playTickSound = () => {
    if (shouldPlaySound && audioPool.current.length > 0) {
      const audio = audioPool.current[poolIdx.current];
      audio.currentTime = 0;
      const p = audio.play();
      if (p !== undefined) p.catch(() => {});
      poolIdx.current = (poolIdx.current + 1) % audioPool.current.length;
    }
  };

  useEffect(() => {
    if (!activeCase) return;

    if (!spunItem) {
      const items = Array.from({ length: 80 }).map(() =>
        getWeightedRandomItem(activeCase.items)
      );
      setStrip(items);
      setPhase("idle");
      return;
    }

    const items = Array.from({ length: 80 }).map((_, i) =>
      i === 75 ? spunItem : getWeightedRandomItem(activeCase.items)
    );
    setStrip(items);

    const t1 = setTimeout(() => setPhase("rolling"),  120);
    const t3 = setTimeout(() => setPhase("finished"), 4100);
    return () => { clearTimeout(t1); clearTimeout(t3); };
  }, [activeCase?.id, spunItem]); // eslint-disable-line

  const ITEM_WIDTH       = 132;
  const TARGET_INDEX     = 75;
  const slotContainerRef = useRef(null);

  const getCenterOffset = () => {
    const w = slotContainerRef.current?.offsetWidth || 800;
    return Math.max(0, w / 2 - ITEM_WIDTH / 2);
  };

  const landingX = useRef(0);

  useEffect(() => {
    if (phase !== "idle" || !spinnerRef.current) return;
    const el = spinnerRef.current;
    el.getAnimations().forEach((a) => a.cancel());
    el.style.transform = `translateX(${getCenterOffset()}px)`;
  }, [phase]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "rolling" || !spinnerRef.current || strip.length === 0) return;
    const el = spinnerRef.current;
    const co = getCenterOffset();
    const xTarget = -(TARGET_INDEX * ITEM_WIDTH) + co + randomOffset.current;
    landingX.current = xTarget;

    el.getAnimations().forEach((a) => a.cancel());
    el.style.transform = `translateX(${co}px)`;
    el.animate(
      [
        { transform: `translateX(${co}px)` },
        { transform: `translateX(${xTarget}px)` },
      ],
      { duration: 3900, easing: "cubic-bezier(0.05,0.7,0.1,1)", fill: "forwards" }
    );
  }, [phase, strip]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "finished" || !spinnerRef.current) return;
    const el = spinnerRef.current;
    const co = getCenterOffset();
    const exactCenter = -(TARGET_INDEX * ITEM_WIDTH) + co;
    const fromX = landingX.current;

    el.style.transform = `translateX(${fromX}px)`;
    el.getAnimations().forEach((a) => a.cancel());

    const diff = Math.abs(fromX - exactCenter);
    if (diff > 1) {
      const settle = el.animate(
        [
          { transform: `translateX(${fromX}px)` },
          { transform: `translateX(${exactCenter}px)` },
        ],
        {
          duration: Math.max(150, Math.min(500, diff * 10)),
          easing: "cubic-bezier(0.25,0.46,0.45,0.94)",
          fill: "forwards",
        }
      );
      settle.onfinish = () => {
        el.style.transform = `translateX(${exactCenter}px)`;
        el.getAnimations().forEach((a) => a.cancel());
      };
    }

    Array.from(el.children).forEach((c) => {
      c.style.transform = "";
      c.style.opacity   = "";
      c.style.filter    = "";
    });
  }, [phase]); // eslint-disable-line

  useEffect(() => {
    if (phase !== "rolling") return;
    const co = getCenterOffset();
    let animId;
    let lastIdx = -1;
    const tick = () => {
      if (!spinnerRef.current) return;
      const mat = window.getComputedStyle(spinnerRef.current).transform;
      const currentX = mat !== "none" ? (parseFloat(mat.split(",")[4]) || 0) : co;
      const idx = Math.max(0, Math.floor((co - currentX + ITEM_WIDTH / 2) / ITEM_WIDTH));
      if (lastIdx !== -1 && idx > lastIdx) playTickSound();
      lastIdx = idx;
      if (spinnerRef.current?.children) {
        Array.from(spinnerRef.current.children).forEach((child, i) => {
          child.style.transform = i === idx ? "scale(1.10)" : "scale(0.92)";
          child.style.opacity   = i === idx ? "1" : "0.55";
        });
      }
      animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [phase]); // eslint-disable-line

  return (
    <div
      ref={slotContainerRef}
      className="relative h-[100px] sm:h-36 overflow-hidden rounded-2xl border border-white/[0.06] bg-black/30 shadow-inner"
    >
      <div
        ref={spinnerRef}
        className="absolute left-0 top-0 flex h-full items-center"
      >
        {strip.map((item, i) => (
          <div
            key={i}
            style={{ width: `${ITEM_WIDTH}px` }}
            className={`flex h-full shrink-0 items-center justify-center px-1 transition-all ${
              phase === "finished" ? "duration-500" : "duration-[100ms] ease-out"
            } ${
              i === TARGET_INDEX && phase === "finished"
                ? "relative z-10 scale-[1.10] drop-shadow-[0_0_20px_rgba(16,185,129,0.8)]"
                : ""
            }`}
          >
            <div
              className={`relative flex h-[88px] sm:h-[112px] w-[124px] flex-col items-center justify-center overflow-hidden rounded-xl border bg-black/50 p-2 ${
                i === TARGET_INDEX && phase === "finished"
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : "border-white/[0.06]"
              }`}
              style={{ borderBottom: `4px solid ${item.color || "#4ade80"}` }}
            >
              <div
                className="absolute inset-0 opacity-15"
                style={{ background: `radial-gradient(circle at center,${item.color || "#4ade80"} 0%,transparent 70%)` }}
              />
              <img src={item.image_url} alt={item.name} className="relative z-10 mb-1 h-12 w-12 object-contain drop-shadow-lg" />
              <span className="relative z-10 w-full truncate text-center text-[10px] font-bold text-white">{item.name}</span>
              <span className="relative z-10 mt-0.5 mm-num text-[10px] text-emerald-400">{formatAmount(item.price)}<RobuxIcon size={13} className="ml-1" /></span>
            </div>
          </div>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-y-0 left-1/2 z-20 w-0.5 -translate-x-1/2 bg-gold-400 shadow-[0_0_14px_rgba(255,182,39,0.9)]" />

      {phase === "finished" && (
        <div className="pointer-events-none absolute inset-0 z-10 flex animate-pulse items-center justify-center transition-opacity duration-500">
          <div className="h-full w-[124px] border-x border-emerald-500/30 bg-emerald-500/10" />
        </div>
      )}

      <div className="pointer-events-none absolute inset-y-0 left-0 z-30 w-24 bg-gradient-to-r from-[#020307] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-30 w-24 bg-gradient-to-l from-[#020307] to-transparent" />
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   LEVEL CASE CARD
   ────────────────────────────────────────────────────────────── */
const LevelCaseCard = ({ caseData, onClick }) => {
  const status = caseData.user_status || {};
  const unlocked = !!status.unlocked;
  const ready    = !!status.ready;
  const onCooldown = !!status.on_cooldown;
  const requiredLevel = caseData.required_level;

  const [cooldownText, setCooldownText] = useState(
    onCooldown ? calculateCooldown(status.next_opening_at) : ""
  );

  useEffect(() => {
    if (!onCooldown || !status.next_opening_at) return;
    const t = setInterval(() => {
      setCooldownText(calculateCooldown(status.next_opening_at));
    }, 1000);
    return () => clearInterval(t);
  }, [onCooldown, status.next_opening_at]);

  const ringColor = ready
    ? "border-emerald-500/40 hover:border-emerald-400/60 hover:shadow-[0_0_24px_rgba(16,185,129,0.20)]"
    : unlocked
      ? "border-amber-500/25 hover:border-amber-500/40"
      : "border-white/[0.05]";

  const isClickable = unlocked;

  return (
    <motion.div
      whileHover={isClickable ? { y: -4, scale: 1.02 } : {}}
      onClick={() => isClickable && onClick(caseData)}
      className={`group relative flex flex-col items-center overflow-hidden rounded-2xl border bg-gradient-to-b from-white/[0.04] to-transparent p-4 shadow-lg transition-all ${ringColor} ${
        isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-60"
      }`}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

      <div className={`absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full border px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-wider ${
        ready ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : unlocked ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
              : "border-white/[0.08] bg-white/[0.04] text-white/40"
      }`}>
        <Trophy className="h-2.5 w-2.5" /> Lvl {requiredLevel}
      </div>

      <div className="absolute right-2 top-2 z-20">
        {!unlocked && (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-white/[0.08] bg-black/40">
            <Lock className="h-3 w-3 text-white/40" />
          </div>
        )}
        {ready && (
          <div className="flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 font-display text-[8px] font-black uppercase tracking-wider text-emerald-300">
            <Sparkles className="h-2.5 w-2.5" /> Ready
          </div>
        )}
      </div>

      <div className={`relative mb-3 mt-4 flex h-24 w-full items-center justify-center pointer-events-none transition-all ${!unlocked ? "grayscale" : ""}`}>
        <div className="absolute inset-0 rounded-full bg-gold-500/[0.04] blur-2xl transition-colors group-hover:bg-gold-500/[0.10]" />
        {caseData.image_url?.startsWith("color:") ? (
          <div className="relative z-10 h-20 w-20 transition-transform duration-300 group-hover:scale-110">
            <DynamicCasePreview caseColor={caseData.image_url} items={caseData.items || []} />
          </div>
        ) : caseData.image_url ? (
          <img src={caseData.image_url} alt={caseData.name} className="relative z-10 max-h-full object-contain drop-shadow-2xl transition-transform duration-300 group-hover:scale-110" />
        ) : (
          <div className="relative z-10 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/[0.06] bg-white/[0.03]">
            <Gift className="h-10 w-10 text-white/30" />
          </div>
        )}
      </div>

      <h3 className="w-full truncate px-2 text-center font-display text-sm font-black uppercase tracking-wide text-white" title={caseData.name}>
        {caseData.name}
      </h3>

      <div className="mt-2 flex h-5 items-center justify-center mm-num text-[10px] font-bold">
        {!unlocked && <span className="text-white/30">Reach Lvl {requiredLevel}</span>}
        {unlocked && onCooldown && (
          <span className="flex items-center gap-1 text-amber-400/80">
            <Clock className="h-3 w-3" /> {cooldownText}
          </span>
        )}
        {ready && (
          <span className="text-emerald-400">Free Daily Open</span>
        )}
      </div>
    </motion.div>
  );
};

/* ──────────────────────────────────────────────────────────────
   LEVEL CASE MODAL
   ────────────────────────────────────────────────────────────── */
const LevelCaseModal = ({ caseData, onClose, token, onOpened }) => {
  const [spinning, setSpinning]         = useState(false);
  const [spunItem, setSpunItem]         = useState(null);
  const [wonItem, setWonItem]           = useState(null);
  const [spinKey, setSpinKey]           = useState(0);
  const [clientSeed, setClientSeed]     = useState(() => Math.random().toString(36).substring(2, 10));
  const [provablyFairData, setProvablyFairData] = useState(null);

  const [localStatus, setLocalStatus] = useState(caseData.user_status || {});
  const [cooldownText, setCooldownText] = useState(
    localStatus.on_cooldown ? calculateCooldown(localStatus.next_opening_at) : ""
  );

  useEffect(() => {
    setLocalStatus(caseData.user_status || {});
  }, [caseData]);

  useEffect(() => {
    if (!localStatus.on_cooldown || !localStatus.next_opening_at) {
      setCooldownText("");
      return;
    }
    setCooldownText(calculateCooldown(localStatus.next_opening_at));
    const t = setInterval(() => {
      const txt = calculateCooldown(localStatus.next_opening_at);
      setCooldownText(txt);
      if (txt === "Ready") {
        setLocalStatus((s) => ({ ...s, on_cooldown: false, ready: !!s.unlocked, next_opening_at: null }));
      }
    }, 1000);
    return () => clearInterval(t);
  }, [localStatus.on_cooldown, localStatus.next_opening_at]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !spinning) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [spinning, onClose]);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, []);

  const sortedByRarity = useMemo(() => {
    // En nadir (en düşük chance/weight) en solda → ASC sıralama
    const items = [...(caseData.items || [])];
    return items.sort((a, b) => {
      const ac = (a.chance ?? a.weight ?? 0);
      const bc = (b.chance ?? b.weight ?? 0);
      return ac - bc;
    });
  }, [caseData.items]);

  const handleOpen = async () => {
    if (spinning) return;
    if (!localStatus.unlocked) {
      toast.error(`You need Level ${caseData.required_level} to open this case.`);
      return;
    }
    if (localStatus.on_cooldown) {
      toast.error("This case is on cooldown.");
      return;
    }

    setWonItem(null);
    setSpunItem(null);
    setSpinning(true);

    try {
      const response = await axios.post(
        `${API}/game/level-cases/open`,
        { case_id: caseData.id, client_seed: clientSeed },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProvablyFairData({
        serverSeed: response.data.server_seed,
        clientSeed: response.data.client_seed,
      });
      setClientSeed(Math.random().toString(36).substring(2, 10));

      const winningItemData = response.data.unboxed?.[0];
      const realWinningItem = winningItemData
        ? (caseData.items.find((it) => it.name === winningItemData.name) || winningItemData)
        : null;

      setSpinKey((k) => k + 1);
      setSpunItem(realWinningItem);

      setTimeout(() => {
        setSpinning(false);
        setWonItem(realWinningItem);
        setLocalStatus((s) => ({
          ...s,
          ready: false,
          on_cooldown: true,
          next_opening_at: response.data.next_opening_at,
        }));
        if (onOpened) onOpened(caseData.id, response.data.next_opening_at);
      }, 4500);
    } catch (error) {
      setSpinning(false);
      setSpunItem(null);
      toast.error(error.response?.data?.detail || "Failed to open case.");
      if (onOpened) onOpened(caseData.id, null, true);
    }
  };

  const buttonState = spinning
    ? "spinning"
    : !localStatus.unlocked ? "locked"
    : localStatus.on_cooldown ? "cooldown"
    : "ready";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-3 backdrop-blur-md sm:p-6"
      onClick={() => !spinning && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/[0.08] bg-[#05070d] shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" />

        <button
          onClick={() => !spinning && onClose()}
          disabled={spinning}
          aria-label="Close"
          className="absolute right-4 top-4 z-50 flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-black/50 text-white/60 transition-all hover:border-white/[0.15] hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="overflow-y-auto scrollbar-thin">
          <div className="relative border-b border-white/[0.05] bg-[#020307]/60 p-4 pt-12">
            <HorizontalSlotSpinner
              key={`${caseData.id}-${spinKey}`}
              activeCase={caseData}
              spunItem={spunItem}
              shouldPlaySound={true}
            />
          </div>

          <div className="flex flex-col items-center justify-center gap-4 bg-white/[0.02] p-6">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/10 px-3 py-1 font-display text-[9px] font-black uppercase tracking-[0.25em] text-gold-300">
                <Trophy className="h-3 w-3" /> Level {caseData.required_level} · Daily Free
              </div>
              <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                {caseData.name}
              </h2>
            </div>

            <AnimatePresence>
              {wonItem && !spinning && (
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex items-center gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.08] p-3 pr-5"
                >
                  <img src={wonItem.image_url} alt="Won" className="h-12 w-12 object-contain drop-shadow-lg" />
                  <div>
                    <p className="font-display text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-400">You Won</p>
                    <p className="max-w-[180px] truncate font-display text-sm font-black text-white">{wonItem.name}</p>
                    <p className="mm-num text-xs font-bold text-emerald-400">+{formatAmount(wonItem.price)}<RobuxIcon size={13} className="ml-1" /></p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              whileHover={buttonState === "ready" ? { y: -2 } : {}}
              whileTap={buttonState === "ready" ? { scale: 0.97 } : {}}
              onClick={handleOpen}
              disabled={buttonState !== "ready"}
              className={`group relative overflow-hidden rounded-2xl px-10 py-4 font-display text-base font-black uppercase tracking-[0.18em] transition-all sm:text-lg ${
                buttonState === "ready"
                  ? "bg-[#ffb627] text-[#110a00] shadow-[0_8px_24px_-4px_rgba(255,182,39,0.5)] hover:bg-[#ffc24d] hover:shadow-[0_12px_28px_-4px_rgba(255,182,39,0.65)]"
                  : "cursor-not-allowed border border-white/[0.06] bg-white/[0.03] text-white/30"
              }`}
            >
              {buttonState === "ready" && (
                <div className="pointer-events-none absolute inset-0 translate-y-full bg-white/20 transition-transform duration-300 ease-in-out group-hover:translate-y-0" />
              )}
              <span className="relative z-10 flex items-center justify-center gap-3">
                {buttonState === "spinning" && (<><Loader2 className="h-5 w-5 animate-spin" /> Opening…</>)}
                {buttonState === "locked"   && (<><Lock className="h-5 w-5" /> Locked</>)}
                {buttonState === "cooldown" && (<><Clock className="h-4 w-4" /> Available in {cooldownText}</>)}
                {buttonState === "ready"    && (<><Unlock className="h-5 w-5" /> Open Free</>)}
              </span>
            </motion.button>
          </div>

          {/* CASE CONTENTS — sorted RAREST FIRST (leftmost) */}
          <div className="border-t border-white/[0.05] p-6 pb-10">
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="h-4 w-1.5 rounded-full bg-gold-400" />
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.28em] text-white/50">
                  Case Contents
                </span>
              </div>
              <span className="font-display text-[9px] font-bold uppercase tracking-[0.22em] text-white/30">
                Rarest → Most Common
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {sortedByRarity.map((item, idx) => (
                <motion.div
                  key={`${item.item_id || item.name}-${idx}`}
                  whileHover={{ y: -3 }}
                  className="group relative flex flex-col items-center overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.03] p-4 transition-colors hover:border-white/[0.12] hover:bg-white/[0.06]"
                  style={{ borderBottom: `3px solid ${item.color || "#4ade80"}` }}
                >
                  <div className="absolute left-2 top-2 mm-num text-[8px] font-bold text-white/20">#{idx + 1}</div>

                  <img src={item.image_url} alt={item.name} className="mb-3 mt-2 h-12 w-12 object-contain drop-shadow-md transition-transform group-hover:scale-110" />
                  <span className="w-full truncate text-center font-display text-[10px] font-bold text-white/80" title={item.name}>{item.name}</span>

                  <div className="mt-2 flex w-full flex-col items-center gap-1">
                    <span className="mm-num text-[11px] font-black text-emerald-400">{formatAmount(item.price)}<RobuxIcon size={13} className="ml-1" /></span>
                    <span className="rounded-full border border-white/[0.06] bg-black/30 px-2 py-0.5 mm-num text-[9px] font-bold text-white/50">
                      {(item.chance ?? 0).toFixed(2)}%
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* PROVABLY FAIR FOOTER */}
          <div className="border-t border-white/[0.05] bg-[#020307]/40 px-6 py-3">
            <div className="flex flex-col items-center justify-between gap-2 mm-num text-[9px] text-white/25 sm:flex-row sm:text-[10px]">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500/50" />
                <span>Provably Fair</span>
              </div>
              {provablyFairData && !spinning && (
                <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-4">
                  <span className="truncate text-emerald-500/60" title={provablyFairData.serverSeed}>
                    <span className="text-emerald-700/80">Server:</span> {provablyFairData.serverSeed.substring(0, 16)}…
                  </span>
                  <span className="truncate text-emerald-500/60" title={provablyFairData.clientSeed}>
                    <span className="text-emerald-700/80">Client:</span> {provablyFairData.clientSeed}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Rakeback = () => {
  const { user, token, isAuthenticated } = useAuth();
  
  const [rakebackData, setRakebackData] = useState(null);
  // 💬 Aç/kapa durumu paylaşımlı.
  const { chatOpen } = useChatLayout();
  const [isLoading, setIsLoading] = useState(true);
  const [claimingType, setClaimingType] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ daily: "", weekly: "", monthly: "" });

  const [levelCases, setLevelCases]       = useState([]);
  const [levelCasesLoading, setLevelCasesLoading] = useState(true);
  const [selectedLevelCase, setSelectedLevelCase] = useState(null);

  const fetchRakeback = async () => {
    try {
      const response = await axios.get(`${API}/user/rakeback`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if(response.data.success) {
          setRakebackData(response.data);
      }
    } catch (error) {
      toast.error("Failed to load rakeback data.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLevelCases = async () => {
    try {
      const headers = isAuthenticated && token
        ? { Authorization: `Bearer ${token}` }
        : {};
      const res = await axios.get(`${API}/game/level-cases`, { headers });
      setLevelCases(Array.isArray(res.data) ? res.data : []);
    } catch {
      setLevelCases([]);
    } finally {
      setLevelCasesLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchRakeback();
    }
    fetchLevelCases();
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!selectedLevelCase) return;
    const fresh = levelCases.find((c) => c.id === selectedLevelCase.id);
    if (!fresh) return;
    // Reference değil, anlamlı alanları karşılaştır → gereksiz re-render yok
    const cur = selectedLevelCase.user_status || {};
    const nxt = fresh.user_status || {};
    const changed =
      cur.ready !== nxt.ready ||
      cur.on_cooldown !== nxt.on_cooldown ||
      cur.next_opening_at !== nxt.next_opening_at ||
      cur.unlocked !== nxt.unlocked ||
      cur.user_level !== nxt.user_level;
    if (changed) setSelectedLevelCase(fresh);
  }, [levelCases, selectedLevelCase]);

  // Canlı Geri Sayım (Countdown) Sayacı
  useEffect(() => {
    if (!rakebackData) return;

    const timer = setInterval(() => {
      setTimeRemaining({
        daily: calculateTimeLeft(rakebackData.daily.next_claim),
        weekly: calculateTimeLeft(rakebackData.weekly.next_claim),
        monthly: calculateTimeLeft(rakebackData.monthly.next_claim)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rakebackData]);

  const handleClaim = async (type) => {
    if (claimingType) return;
    setClaimingType(type);

    try {
      const response = await axios.post(`${API}/user/rakeback/claim`, { type }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      fetchRakeback(); // Verileri anında güncelle
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to claim ${type} rakeback.`);
    } finally {
      setClaimingType(null);
    }
  };

  const handleLevelCaseOpened = (caseId, nextOpeningAt, errored = false) => {
    if (errored) {
      fetchLevelCases();
      return;
    }
    if (nextOpeningAt) {
      setLevelCases((prev) =>
        prev.map((c) =>
          c.id === caseId
            ? {
                ...c,
                user_status: {
                  ...(c.user_status || {}),
                  ready: false,
                  on_cooldown: true,
                  next_opening_at: nextOpeningAt,
                },
              }
            : c
        )
      );
    }
    setTimeout(() => fetchLevelCases(), 6000);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0E0F11] pt-32 pb-12 flex items-center justify-center">
        <div className="text-center">
          <Gift className="w-16 h-16 text-[#B059E8] mx-auto mb-4 opacity-50" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Login Required</h2>
          <p className="text-zinc-400">Please log in to view your Rakeback rewards.</p>
        </div>
      </div>
    );
  }

  const renderRakebackCard = (type, data, title, desc, Icon, colorClass, borderClass, bgClass, btnClass) => {
    if (!data) return null;
    const isReady = data.ready && data.amount > 0;
    const isPending = claimingType === type;

    return (
      <motion.div
        whileHover={{ y: -4 }}
        className={`rounded-2xl border ${borderClass} ${bgClass} p-4 shadow-xl relative overflow-hidden flex flex-col`}
      >
        <div className="absolute -top-6 -right-6 w-40 h-40 bg-white/5 rounded-full blur-[60px] pointer-events-none" />

        <div className="flex items-center gap-3 mb-2.5 relative z-10">
          <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center border shadow-inner ${colorClass.iconBg} ${colorClass.iconBorder}`}>
            <Icon className={`w-4 h-4 ${colorClass.iconText}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-heading font-bold text-white uppercase tracking-wider truncate">{title}</h3>
            <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">{type} Reward</p>
          </div>
        </div>

        <div className="relative z-10 rounded-xl bg-[#15171a]/60 border border-[#2A2D31]/40 py-3 px-4 mb-2.5 text-center">
          <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-[0.2em] mb-1">Available to Claim</p>
          <p className={`mm-num font-black leading-none text-3xl ${isReady ? colorClass.amountText : 'text-zinc-500'}`}>
            ${formatAmount(data.amount)}
          </p>
          <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] text-zinc-500 font-bold uppercase tracking-widest">
            <Clock className="w-3 h-3" /> Accumulating
            <span className="mm-num text-zinc-300 tracking-normal">+${formatAmount(data.pending_amount)}</span>
          </div>
        </div>

        <button
          onClick={() => handleClaim(type)}
          disabled={!isReady || isPending}
          className={`w-full py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 relative z-10
            ${isReady && !isPending
              ? `${btnClass} text-white shadow-lg transform hover:scale-[1.02]`
              : 'bg-[#2A2D31]/30 text-zinc-500 border border-[#2A2D31] cursor-not-allowed'}`}
        >
          {isPending ? 'Processing...' : isReady ? (
             <><Sparkles className="w-4 h-4" /> Claim Reward</>
          ) : (
            <><Lock className="w-4 h-4" /> {timeRemaining[type]}</>
          )}
        </button>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0E0F11] relative overflow-x-hidden pt-20 sm:pt-24 pb-12" data-testid="rakeback-page">
      <div className="max-w-[1800px] w-full mx-auto px-4 relative">
        <div className={`flex gap-6 w-full items-start transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* 💬 Sohbet App.js'te TEK kopya (ChatDock). */}
          {/* MAIN CONTENT */}
          <main className="flex-1 min-w-0 flex flex-col gap-4">

            {/* ───────────────────────────────────────────────
                REWARDS HERO / INTRO BANNER
               ─────────────────────────────────────────────── */}
            <motion.section
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-3xl border border-[#2A2D31] bg-gradient-to-br from-[#1A1D20] via-[#16181b] to-[#121316] p-6 shadow-2xl"
            >
              {/* Atmospheric glows */}
              <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#B059E8]/15 blur-[90px]" />
              <div className="pointer-events-none absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-gold-500/[0.07] blur-[80px]" />
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
                  backgroundSize: '44px 44px',
                  maskImage: 'radial-gradient(ellipse at top right, black, transparent 70%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at top right, black, transparent 70%)',
                }}
              />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B059E8]/40 to-transparent" />

              <div className="relative z-10">
                {/* Eyebrow */}
                <div className="mb-2.5 flex items-center gap-2">
                  <div className="h-px w-6 bg-gradient-to-r from-gold-300 to-gold-500" />
                  <span className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
                    The Most Rewarding Casino, Period
                  </span>
                </div>

                {/* Headline */}
                <h1 className="font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
                  Up To 43% Rakeback —
                  <span className="bg-gradient-to-r from-[#c98bf0] via-[#B059E8] to-[#7b2fc4] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(176,89,232,0.35)]">
                    {' '}The Highest Anywhere
                  </span>
                </h1>

                {/* Subtext — one line, the three streams */}
                <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-zinc-400">
                  <span className="font-bold text-[#c98bf0]">18% cash rakeback</span>, <span className="font-bold text-emerald-400">15% from free daily cases</span> (Free to open every day — zero wager, forever.) and{' '}
                  <span className="font-bold text-gold-400">10% from Case Battles</span> — stacking to a real <span className="font-bold text-white">43% back</span>, about{' '}
                  <span className="font-bold text-white">3% of every bet</span>. No site gives more.
                </p>

                {/* Compact breakdown row */}
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-white/[0.06] bg-black/25 px-4 py-3 font-display font-black">
                  <span className="flex items-center gap-1.5 text-base text-white">
                    <TrendingUp className="h-4 w-4 text-[#c98bf0]" /> 18%
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">Cash</span>
                  </span>
                  <span className="text-white/20">+</span>
                  <span className="flex items-center gap-1.5 text-base text-white">
                    <Gift className="h-4 w-4 text-emerald-400" /> 15%
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">Free Cases</span>
                  </span>
                  <span className="text-white/20">+</span>
                  <span className="flex items-center gap-1.5 text-base text-white">
                    <Swords className="h-4 w-4 text-gold-400" /> 10%
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/40">Battles</span>
                  </span>
                  <span className="text-white/20">=</span>
                  <span className="flex items-center gap-1.5 text-lg text-gold-300 drop-shadow-[0_0_14px_rgba(255,182,39,0.45)]">
                    <Coins className="h-4 w-4" /> 43%
                  </span>
                </div>

                {/* Extras line */}
                <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
                  <span className="font-bold text-zinc-300">Every 10 levels increases your cashback</span> — maxing out at <span className="text-white">Level 100</span> — and unlocks a brand-new free daily case. On top of the 43%:{' '}
                  <span className="font-bold text-[#c98bf0]">+10% affiliate rewards</span>, plus <span className="font-bold text-emerald-400">Rains from 15M</span> &amp; big Rain Events.
                </p>
              </div>
            </motion.section>

            {/* RAKEBACK KARTLARI */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-[168px] bg-[#1A1D20] rounded-2xl border border-[#2A2D31] opacity-50" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* DAILY CARD */}
                {renderRakebackCard(
                  'daily',
                  rakebackData?.daily,
                  'Daily Cashback',
                  'Every spin, bet, and play counts toward your daily rewards. Claim a cashback every 24 hours, boosted by your level - the more you play, the more you earn back each day.',
                  Flame,
                  { iconBg: 'bg-[#B059E8]/10', iconBorder: 'border-[#B059E8]/20', iconText: 'text-[#c98bf0]', amountText: 'text-[#c98bf0] drop-shadow-[0_0_10px_rgba(176,89,232,0.4)]' },
                  'border-[#B059E8]/30',
                  'bg-gradient-to-b from-[#1A1D20] to-[#15171a]',
                  'bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] shadow-[#B059E8]/25'
                )}

                {/* WEEKLY CARD */}
                {renderRakebackCard(
                  'weekly',
                  rakebackData?.weekly,
                  'Weekly Cashback',
                  'A full week of gameplay pays off. The more you wager, the bigger your weekly cashback. Climb the levels to unlock even higher rewards and cash in big every week.',
                  Calendar,
                  { iconBg: 'bg-[#B059E8]/10', iconBorder: 'border-[#B059E8]/20', iconText: 'text-[#c98bf0]', amountText: 'text-[#c98bf0] drop-shadow-[0_0_10px_rgba(176,89,232,0.4)]' },
                  'border-[#B059E8]/30',
                  'bg-gradient-to-b from-[#1A1D20] to-[#15171a]',
                  'bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] hover:from-[#c98bf0] hover:to-[#B059E8] shadow-[#B059E8]/25'
                )}

                {/* MONTHLY CARD */}
                {renderRakebackCard(
                  'monthly',
                  rakebackData?.monthly,
                  'Monthly Cashback',
                  'Your grind deserves a payday. At the end of the month, all your gameplay adds up to one huge reward. Higher ranks mean higher rewards - making Level 100 players unstoppable.',
                  CalendarDays,
                  { iconBg: 'bg-[#B059E8]/10', iconBorder: 'border-[#B059E8]/20', iconText: 'text-[#c98bf0]', amountText: 'text-[#c98bf0] drop-shadow-[0_0_10px_rgba(176,89,232,0.4)]' },
                  'border-[#B059E8]/30',
                  'bg-gradient-to-b from-[#1A1D20] to-[#15171a]',
                  'bg-gradient-to-r from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0] shadow-[#B059E8]/25'
                )}

              </div>
            )}

            {/* DAILY LEVEL CASES */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}>
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <div className="h-px w-5 bg-gradient-to-r from-gold-300 to-gold-500" />
                    <span className="font-display text-[9px] font-bold uppercase tracking-[0.28em] text-white/40">Level Rewards</span>
                  </div>
                  <h2 className="font-display text-[22px] font-black uppercase leading-none tracking-tight text-white">
                    Daily Level Cases
                  </h2>
                </div>
                <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/15 bg-emerald-500/[0.05] px-3 py-1.5">
                  <Clock className="h-3 w-3 text-emerald-400/70" />
                  <span className="font-display text-[8px] font-bold uppercase tracking-[0.2em] text-emerald-400/70">
                    Free · Resets every 24h
                  </span>
                </div>
              </div>

              {levelCasesLoading ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {[1,2,3,4,5,6].map((i) => (
                    <div key={i} className="h-[210px] animate-pulse rounded-2xl border border-white/[0.05] bg-white/[0.02]" style={{ animationDelay: `${i * 0.05}s` }} />
                  ))}
                </div>
              ) : levelCases.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/[0.06] py-16">
                  <Trophy className="mb-4 h-12 w-12 text-white/20" />
                  <h3 className="font-display text-base font-black uppercase tracking-wide text-white/60">No Level Cases Yet</h3>
                  <p className="mt-2 text-xs text-white/30">Daily level cases will appear here once available.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {levelCases.map((c) => (
                    <LevelCaseCard
                      key={c.id}
                      caseData={c}
                      onClick={(cd) => setSelectedLevelCase(cd)}
                    />
                  ))}
                </div>
              )}
            </motion.div>

          </main>
        </div>
      </div>

      <AnimatePresence>
        {selectedLevelCase && (
          <LevelCaseModal
            key={selectedLevelCase.id}
            caseData={selectedLevelCase}
            onClose={() => setSelectedLevelCase(null)}
            token={token}
            onOpened={handleLevelCaseOpened}
          />
        )}
      </AnimatePresence>

    </div>
  );
};